import React from 'react';
import ReactApexChart from 'react-apexcharts';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Stack,
    Typography,
    Button,
    Chip,
    Divider,
    Avatar,
    Tooltip,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton
} from '@mui/material';
import {
    Download,
    Calendar,
    TrendingUp,
    MousePointer,
    Euro,
    Eye,
    Users,
    Info,
    BarChart3
} from 'lucide-react';

// ---------------- Helpers ----------------
const nf = (n, opt = {}) => new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2, ...opt }).format(n ?? 0);
const pct = (v) => `${nf(v)} %`;

// ---------------- Mock Data ----------------
const mock = {
    periodDaysOptions: [7, 14],
    kpis: {
        budget: 1250,
        impressions: 245000,
        ctr: 1.9,
        freq: 4.2,
        reach: 180500,
        cpc: 0.28,
        cpa: 3.6,
        revenue: 5780
    },
    evolution: {
        categories: Array.from({ length: 16 }, (_, i) => `${i + 1}`),
        depenses: [20, 40, 60, 75, 90, 110, 95, 120, 80, 140, 130, 150, 170, 160, 155, 180],
        impressions: [120, 160, 130, 180, 200, 220, 210, 260, 240, 280, 260, 270, 300, 260, 240, 310],
        clics: [10, 14, 12, 18, 20, 22, 21, 25, 23, 28, 26, 27, 35, 30, 28, 38],
        achats: [1, 2, 2, 2, 3, 3, 3, 4, 4, 5, 4, 5, 6, 6, 5, 7]
    },
    revenuParJour: [240, 260, 280, 320, 300, 340, 360, 380, 420, 400, 440, 460, 500, 520],
    adsets: [
        { name: 'Ad A', spend: 420, ctr: 1.2, cpa: 0.73, roas: 2.55 },
        { name: 'Ad B', spend: 530, ctr: 1.7, cpa: 0.28, roas: 4.10 },
        { name: 'Ad C', spend: 300, ctr: 0.8, cpa: 0.51, roas: 4.10 }
    ],
    creatives: [
        { id: 'cr1', title: 'Visuel #1', metric: 'CTR', value: 1.8, cpa: 2.95, img: 'https://images.unsplash.com/photo-1487956382158-bb926046304a?q=80&w=400&auto=format&fit=crop' },
        { id: 'cr2', title: 'Visuel #2', metric: 'CPR', value: 4.6, cpa: 4.10, img: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?q=80&w=400&auto=format&fit=crop' }
    ],
    audience: { labels: ['18—24', '25—34', '35—44', '45—54', '55+'], values: [36, 48, 32, 18, 12], male: 38, age2534: 36 }
};

// ---------------- Small UI helpers ----------------
function StatCard({ icon, label, value, helper, chipColor = 'default' }) {
    const Icon = icon;
    return (
        <Card sx={{
            height: "100%",
            boxShadow: 0,
            borderRadius: 2.5,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            border: "1px solid #5e17eb1e",
        }}>
            <CardContent>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar sx={{ width: 30, height: 30, bgcolor: 'white', color: 'black' }}>
                        <Icon size={18} />
                    </Avatar>
                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                    {helper && (
                        <Chip size="small" color={chipColor} variant="soft" label={helper} sx={{ mt: 1 }} />
                    )}
                </Stack>
                <Typography variant="h5" sx={{ mt: 1 }}>{value}</Typography>
            </CardContent>
        </Card>
    );
}

// ---------------- Charts Options ----------------
const lineOptions = {
    chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: false } },
    stroke: { width: 3, curve: 'smooth' },
    dataLabels: { enabled: false },
    grid: { strokeDashArray: 3 },
    xaxis: { categories: mock.evolution.categories, labels: { style: { fontSize: '12px' } } },
    yaxis: { labels: { formatter: (v) => nf(v) } },
    legend: { position: 'top', horizontalAlign: 'left' }
};

const areaOptions = {
    chart: { type: 'area', toolbar: { show: false } },
    stroke: { width: 2, curve: 'smooth' },
    dataLabels: { enabled: false },
    grid: { strokeDashArray: 3 },
    xaxis: { categories: mock.evolution.categories.slice(1), labels: { style: { fontSize: '12px' } } },
    yaxis: { labels: { formatter: (v) => `€ ${nf(v)}` } },
    fill: { type: 'gradient', gradient: { shadeIntensity: 0.2, opacityFrom: 0.5, opacityTo: 0.2 } },
    legend: { show: false }
};

const audienceOptions = {
    chart: { type: 'bar', toolbar: { show: false } },
    dataLabels: { enabled: false },
    plotOptions: { bar: { borderRadius: 6, columnWidth: '40%' } },
    xaxis: { categories: mock.audience.labels },
    yaxis: { labels: { formatter: (v) => nf(v) } },
    grid: { strokeDashArray: 3 }
};

// ---------------- Component ----------------
export default function Perfs() {
    const { kpis } = mock;

    return (
        <Box sx={{ p: { xs: 2 } }}>
            

            {/* KPI cards */}
            <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                    <StatCard icon={Euro} label="Budget dépensé" value={`€ ${nf(kpis.budget)}`} />
                </Grid>
                <Grid item xs={12} md={3}>
                    <StatCard icon={Eye} label="Impressions" value={nf(kpis.impressions)} />
                </Grid>
                <Grid item xs={12} md={3}>
                    <StatCard icon={MousePointer} label="CTR" value={pct(kpis.ctr)} helper="Trop élevé" chipColor="warning" />
                </Grid>
                <Grid item xs={12} md={3}>
                    <StatCard icon={BarChart3} label="Fréquence" value={`Fréquence ${kpis.freq}`} />
                </Grid>

                <Grid item xs={12} md={3}>
                    <StatCard icon={Users} label="Portée" value={nf(kpis.reach)} />
                </Grid>
                <Grid item xs={12} md={3}>
                    <StatCard icon={MousePointer} label="CPC" value={`€ ${nf(kpis.cpc)}`} />
                </Grid>
                <Grid item xs={12} md={3}>
                    <StatCard icon={TrendingUp} label="Coût par achat" value={`€ ${nf(kpis.cpa)}`} />
                </Grid>
                <Grid item xs={12} md={3}>
                    <StatCard icon={Euro} label="Revenu" value={`€ ${nf(kpis.revenue)}`} />
                </Grid>
            </Grid>

            {/* Charts */}
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} md={8}>
                    <Card sx={{ borderRadius: 3 }}>
                        <CardContent>
                            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Évolution quotidienne — Dépenses / Impressions & Clics / Achats</Typography>
                            <ReactApexChart
                                options={{
                                    ...lineOptions,
                                    tooltip: { shared: true },
                                }}
                                series={[
                                    { name: 'Dépenses', data: mock.evolution.depenses },
                                    { name: 'Impressions', data: mock.evolution.impressions },
                                    { name: 'Clics', data: mock.evolution.clics },
                                    { name: 'Achats', data: mock.evolution.achats },
                                ]}
                                type="line"
                                height={300}
                            />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: 3 }}>
                        <CardContent>
                            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Revenu par jour</Typography>
                            <ReactApexChart
                                options={areaOptions}
                                series={[{ name: 'Revenu', data: mock.revenuParJour }]}
                                type="area"
                                height={300}
                            />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Tables & Audience */}
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: 3 }}>
                        <CardContent>
                            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Top ensembles de publicités</Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Adset</TableCell>
                                        <TableCell align="right">Spend</TableCell>
                                        <TableCell align="right">CTR</TableCell>
                                        <TableCell align="right">CPA</TableCell>
                                        <TableCell align="right">ROAS</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {mock.adsets.map((r) => (
                                        <TableRow key={r.name} hover>
                                            <TableCell>{r.name}</TableCell>
                                            <TableCell align="right">€ {nf(r.spend)}</TableCell>
                                            <TableCell align="right">{pct(r.ctr)}</TableCell>
                                            <TableCell align="right">€ {nf(r.cpa)}</TableCell>
                                            <TableCell align="right">{nf(r.roas)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: 3 }}>
                        <CardContent>
                            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Créatifs les plus performants</Typography>
                            <Stack spacing={1.5}>
                                {mock.creatives.map((c) => (
                                    <Stack key={c.id} direction="row" spacing={1.5} alignItems="center">
                                        <Box sx={{ width: 64, height: 42, borderRadius: 1, overflow: 'hidden', bgcolor: '#eee', flexShrink: 0 }}>
                                            <img src={c.img} alt={c.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        </Box>
                                        <Stack sx={{ flex: 1 }}>
                                            <Typography variant="body1">{c.title}</Typography>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                                <Chip size="small" label={`${c.metric}`} />
                                                <Chip size="small" label={`${nf(c.value)} %`} variant="outlined" />
                                                <Chip size="small" label={`CPA € ${nf(c.cpa)}`} variant="soft" />
                                            </Stack>
                                        </Stack>
                                        <Tooltip title="Voir les détails"><IconButton><Info size={18} /></IconButton></Tooltip>
                                    </Stack>
                                ))}
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: 3 }}>
                        <CardContent>
                            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Audience</Typography>
                            <ReactApexChart
                                options={audienceOptions}
                                series={[{ name: 'Utilisateurs', data: mock.audience.values }]}
                                type="bar"
                                height={240}
                            />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: 3, height: '100%' }}>
                        <CardContent>
                            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Répartition</Typography>
                            <Stack direction="row" spacing={3} alignItems="center">
                                <Stack alignItems="center" spacing={0.5}>
                                    <Users />
                                    <Typography variant="h6">{mock.audience.male}</Typography>
                                    <Typography variant="body2" color="text.secondary">Hommes (%)</Typography>
                                </Stack>
                                <Divider orientation="vertical" flexItem />
                                <Stack alignItems="center" spacing={0.5}>
                                    <BarChart3 />
                                    <Typography variant="h6">{mock.audience.age2534}</Typography>
                                    <Typography variant="body2" color="text.secondary">25–34 (%)</Typography>
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
                Données fictives pour aperçu. Intégration prévue: Meta Marketing API v20+ via Cloud Functions Firebase.
            </Typography>
        </Box>
    );
}

// ---------------- Usage ----------------
// 1) Installer les dépendances :
//    npm i @mui/material @emotion/react @emotion/styled lucide-react apexcharts react-apexcharts
// 2) Importer et utiliser <FacebookCampaignSummary /> dans votre route / page organiser.
// 3) Pour brancher aux vraies données, remplacez "mock" par des props ou par un hook qui
//    lit Firestore / appelle une Cloud Function (ex: getCampaignInsights) et met à jour les séries.
